import type { TranslationData } from "../i18n/types";
import type RestClient from "./client";
import type {
  User,
  UserNew,
  App,
  AppIn,
  Campaign,
  CampaignPatch,
  CampaignNewIn,
  CampaignCloneOptions,
  CampaignExistingId,
  MediaFile,
  MediaFolder,
  UploadFileIn,
  UserGeo,
  AppleAlbum,
  ApplePlaylist,
  APINewsletterSignupParams,
  CountryTranslations,
  AccountTokenInfo,
  CampaignAccount,
} from "./types";
import { parseAppleId, parseSpotifyId } from "../parsers";
import type {
  TokenGateCheckResponse,
  TokenGateCredentials,
  TokenGateProvider,
} from "../types";
import { chunked } from "../util";

export class RestModule {
  client: RestClient;
  constructor(client: RestClient) {
    this.client = client;
  }
}

export class Apps extends RestModule {
  async getApps(): Promise<App[]> {
    const resp = await this.client.get("/api/apps");
    return (await resp.json()).items;
  }

  async createApp(data: AppIn): Promise<App> {
    const resp = await this.client.post("/api/apps", { json: data });
    return await resp.json();
  }

  async reloadConfig(appId: string): Promise<App> {
    const resp = await this.client.post(`/api/apps/${appId}/config/reload`);
    return await resp.json();
  }
}

export class Users extends RestModule {
  async getCurrentUser(): Promise<User> {
    const resp = await this.client.get("/api/users/me");
    return await resp.json();
  }

  async createUser(user: UserNew): Promise<void> {
    await this.client.post("/api/users", {
      json: user,
      includeToken: false,
    });
  }

  async updateUserRoles(userId: string, roles: string[]): Promise<void> {
    await this.client.post(`/api/users/${userId}/roles`, { json: { roles } });
  }

  async updateUserState(userId: string, state: string): Promise<void> {
    await this.client.post(`/api/users/${userId}/state`, { json: { state } });
  }

  async updateUserPassword(password: string, token: string): Promise<void> {
    await this.client.put("/api/users/password", {
      json: { new_password: password, token },
      includeToken: false,
    });
  }

  async resetUserPassword(email: string): Promise<void> {
    await this.client.post("/api/users/password_reset", {
      json: { email, cms: true },
      includeToken: false,
    });
  }

  async getAllUsers(): Promise<User[]> {
    const resp = await this.client.get("/api/users");
    return await resp.json();
  }
}

export class Campaigns extends RestModule {
  async getAllCampaigns(): Promise<Campaign[]> {
    const resp = await this.client.get("/api/campaigns");
    return (await resp.json()).items;
  }

  async getCampaign(campaignID: string): Promise<Campaign> {
    const resp = await this.client.get(`/api/campaigns/${campaignID}`);
    return await resp.json();
  }

  async patchCampaign(
    campaignID: string,
    data: CampaignPatch
  ): Promise<Campaign> {
    const resp = await this.client.post(`/api/campaigns/${campaignID}/patch`, {
      json: data,
    });
    return await resp.json();
  }

  async createCampaign(data: CampaignNewIn): Promise<Campaign> {
    const resp = await this.client.post("/api/campaigns", { json: data });
    return await resp.json();
  }

  async cloneCampaign(
    campaignID: string,
    options: CampaignCloneOptions = {}
  ): Promise<Campaign> {
    const resp = await this.client.post(`/api/campaigns/${campaignID}/clone`, {
      json: options,
    });
    return await resp.json();
  }

  async slugExists(appId: string, slug: string): Promise<CampaignExistingId> {
    const resp = await this.client.get(
      `/api/campaigns/${appId}/${slug}/exists`
    );
    return await resp.json();
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await this.client.delete(`/api/campaigns/${campaignId}`);
  }

  async lockCampaign(campaignId: string): Promise<void> {
    await this.client.put(`/api/campaigns/${campaignId}/lock`);
  }

  async unlockCampaign(campaignId: string): Promise<void> {
    await this.client.delete(`/api/campaigns/${campaignId}/lock`);
  }
}

export class Uploads extends RestModule {
  async getAllUploads(): Promise<MediaFile[]> {
    const resp = await this.client.get("/api/uploads");
    return (await resp.json()).items;
  }

  async createUpload(data: UploadFileIn): Promise<MediaFile> {
    const form = new FormData();
    form.append("file", data.file);
    form.append("folder", data.folder || "/");

    const resp = await this.client.post("/api/uploads", { body: form });
    return await resp.json();
  }

  async deleteUpload(filename: string): Promise<void> {
    await this.client.delete(`/api/uploads/${filename}`);
  }

  async deleteFolder(pathname: string): Promise<void> {
    await this.client.delete(`/api/uploads/folders${pathname}`);
  }

  async createFolder(pathname: string): Promise<void> {
    await this.client.post(`/api/uploads/folders${pathname}`);
  }

  async getFolder(pathname: string, byUrl = false): Promise<MediaFolder> {
    const url = byUrl
      ? "/api/uploads/folders"
      : `/api/uploads/folders${pathname}`;
    const options = byUrl ? { params: { file_url: pathname } } : {};
    const resp = await this.client.get(url, options);
    return await resp.json();
  }
}

export class Spotify extends RestModule {
  async getArtist(
    query: string,
    params: Record<string, string> = {}
  ): Promise<SpotifyApi.ArtistObjectFull> {
    const artistId = parseSpotifyId(query);
    const resp = await this.client.get(`/api/spotify/artists/${artistId}`, {
      params: params,
      includeToken: false,
    });
    return await resp.json();
  }

  async getAlbum(
    query: string,
    params: Record<string, string> = {}
  ): Promise<SpotifyApi.AlbumObjectFull> {
    const albumId = parseSpotifyId(query);
    const resp = await this.client.get(`/api/spotify/albums/${albumId}`, {
      params: params,
      includeToken: false,
    });
    return await resp.json();
  }

  async getPlaylist(
    query: string,
    params: Record<string, string> = {}
  ): Promise<SpotifyApi.PlaylistObjectFull> {
    const playlistId = parseSpotifyId(query);
    const resp = await this.client.get(`/api/spotify/playlists/${playlistId}`, {
      params: params,
      includeToken: false,
    });
    return await resp.json();
  }

  async getTrack(
    query: string,
    params: Record<string, string> = {}
  ): Promise<SpotifyApi.TrackObjectFull> {
    const trackId = parseSpotifyId(query);
    const resp = await this.client.get(`/api/spotify/tracks/${trackId}`, {
      params: params,
      includeToken: false,
    });
    return await resp.json();
  }

  async getSeveralTracks(
    trackIds: string[]
  ): Promise<SpotifyApi.TrackObjectFull[]> {
    const result: SpotifyApi.TrackObjectFull[] = [];
    // Load track in chunks of 50
    for (const chunk of chunked(trackIds, 50)) {
      const resp = await this.client.get("/api/spotify/tracks", {
        params: { ids: chunk.join(",") },
        includeToken: false,
      });
      const data = await resp.json();
      result.push(...data.tracks);
    }
    return result;
  }
}

export class Apple extends RestModule {
  async getAlbum(
    urlOrId: string,
    params: { storefront?: string | null }
  ): Promise<AppleAlbum> {
    const info = parseAppleId(urlOrId, params.storefront);
    const resp = await this.client.get(
      `/api/apple/albums/${info.storefront}/${info.id}`,
      {
        includeToken: false,
      }
    );
    return await resp.json();
  }

  async getPlaylist(
    urlOrId: string,
    params: { storefront?: string | null }
  ): Promise<ApplePlaylist> {
    const info = parseAppleId(urlOrId, params.storefront);
    const resp = await this.client.get(
      `/api/apple/playlists/${info.storefront}/${info.id}`,
      {
        includeToken: false,
      }
    );
    return await resp.json();
  }
}

export class I18n extends RestModule {
  async getCampaignTranslations(campaignId: string): Promise<TranslationData> {
    const resp = await this.client.get(
      `/api/campaigns/${campaignId}/translations`,
      { includeToken: true }
    );
    return await resp.json();
  }
  async getAppTranslations(
    app: string,
    fresh = false
  ): Promise<TranslationData> {
    const resp = await this.client.get(`/api/apps/${app}/translations`, {
      includeToken: false,
      params: {
        fresh,
      },
    });
    return await resp.json();
  }
  async getUserGeo(): Promise<UserGeo> {
    const resp = await this.client.get("/api/i18n/geo", {
      includeToken: false,
    });
    return await resp.json();
  }
  async getCountries(): Promise<CountryTranslations> {
    const resp = await this.client.get("/api/i18n/countries", {
      includeToken: false,
    });
    return await resp.json();
  }
}

export class Newsletter extends RestModule {
  async signup(
    campaignId: string,
    params: APINewsletterSignupParams
  ): Promise<void> {
    await this.client.post(`/api/campaigns/${campaignId}/newsletter/signup`, {
      json: params,
      includeToken: false,
    });
  }
}

export class Accounts extends RestModule {
  loginUrl(
    campaignId: string,
    service: string,
    params: {
      redirect_uri?: string;
      hash_output?: boolean;
      state?: string;
    } = {}
  ): string {
    const url = new URL(
      `/api/campaigns/${campaignId}/auth/${service}/login`,
      this.client.getOrigin()
    );
    for (const [key, value] of Object.entries(params)) {
      if (!value) {
        continue;
      }
      url.searchParams.append(key, value.toString());
    }
    return url.toString();
  }

  async loginApple(
    campaignId: string,
    musicUserToken: string
  ): Promise<AccountTokenInfo> {
    const resp = await this.client.post(
      `/api/campaigns/${campaignId}/auth/apple/login`,
      {
        json: { music_user_token: musicUserToken },
        includeToken: false,
      }
    );
    return await resp.json();
  }

  async refreshLogin(refreshToken: string): Promise<AccountTokenInfo> {
    const resp = await this.client.post("/api/accounts/session/refresh", {
      includeToken: false,
      headers: { authorization: refreshToken },
    });
    return await resp.json();
  }

  async logout(refreshToken: string): Promise<void> {
    await this.client.delete("/api/accounts/session", {
      includeToken: false,
      headers: { authorization: refreshToken },
    });
  }

  async me(token: string): Promise<CampaignAccount> {
    const resp = await this.client.get("/api/accounts/me", {
      includeToken: false,
      headers: { authorization: "Bearer " + token },
    });
    return await resp.json();
  }
}

export class TokenGate extends RestModule {
  async providerCheck(
    campaignId: string,
    credentials: TokenGateCredentials
  ): Promise<TokenGateCheckResponse> {
    const url = `/api/campaigns/${campaignId}/token_gate/provider_check`;
    const payload = { credentials };

    const resp = await this.client.post(url, {
      includeToken: false,
      json: payload,
    });

    return await resp.json();
  }

  async verifyAccessKey(
    campaign_id: string,
    access_key: string
  ): Promise<boolean> {
    const url = `/api/campaigns/${campaign_id}/token_gate/verify_access`;
    const headers = {
      "token-gate-access-key": access_key,
    };
    const resp = await this.client.get(url, { includeToken: false, headers });
    return (await resp.json()).has_access;
  }
}
